import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './config';
import {
  Permission,
  PermissionCategory,
  RoleDefinition,
  RolePermissions,
  PartnerRole,
  PermissionChangeLog,
  PermissionTemplate,
  DataAccessLevel,
  PERMISSION_RESOURCES,
  PERMISSION_ACTIONS,
  DEFAULT_PERMISSION_CATEGORIES
} from '../../types/permission.types';
import { UserRole } from '../../types/user.types';

// Cache for permissions to reduce Firestore reads
const permissionCache = new Map<string, any>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Clear cache entry after TTL
const clearCacheEntry = (key: string) => {
  setTimeout(() => {
    permissionCache.delete(key);
  }, CACHE_TTL);
};

// Get all permission categories
export const getPermissionCategories = async (): Promise<PermissionCategory[]> => {
  const cacheKey = 'permission_categories';
  
  if (permissionCache.has(cacheKey)) {
    return permissionCache.get(cacheKey);
  }

  try {
    const categoriesQuery = query(
      collection(db, 'permissionCategories'),
      orderBy('order', 'asc')
    );
    
    const snapshot = await getDocs(categoriesQuery);
    
    if (snapshot.empty) {
      // Initialize with default categories if none exist
      await initializeDefaultCategories();
      return DEFAULT_PERMISSION_CATEGORIES;
    }
    
    const categories = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as PermissionCategory));
    
    permissionCache.set(cacheKey, categories);
    clearCacheEntry(cacheKey);
    
    return categories;
  } catch (error) {
    console.error('Error fetching permission categories:', error);
    return DEFAULT_PERMISSION_CATEGORIES;
  }
};

// Get all permissions
export const getAllPermissions = async (): Promise<Permission[]> => {
  const cacheKey = 'all_permissions';
  
  if (permissionCache.has(cacheKey)) {
    return permissionCache.get(cacheKey);
  }

  try {
    // Simple query without ordering to avoid index requirement
    const permissionsQuery = collection(db, 'permissions');
    const snapshot = await getDocs(permissionsQuery);
    
    if (snapshot.empty) {
      // Initialize with default permissions if none exist
      await initializeDefaultPermissions();
      return await getAllPermissions(); // Recursive call after initialization
    }
    
    const permissions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Permission));
    
    // Sort in memory instead of in the query
    permissions.sort((a, b) => {
      if (a.categoryId !== b.categoryId) {
        return (a.categoryId || '').localeCompare(b.categoryId || '');
      }
      return (a.displayName || '').localeCompare(b.displayName || '');
    });
    
    permissionCache.set(cacheKey, permissions);
    clearCacheEntry(cacheKey);
    
    return permissions;
  } catch (error) {
    console.error('Error fetching permissions:', error);
    return [];
  }
};

// Get permissions for a specific role
export const getRolePermissions = async (roleId: string): Promise<RolePermissions | null> => {
  const cacheKey = `role_permissions_${roleId}`;
  
  if (permissionCache.has(cacheKey)) {
    return permissionCache.get(cacheKey);
  }

  try {
    const rolePermDoc = await getDoc(doc(db, 'rolePermissions', roleId));
    
    if (!rolePermDoc.exists()) {
      return null;
    }
    
    const rolePermissions = {
      id: rolePermDoc.id,
      ...rolePermDoc.data()
    } as RolePermissions;
    
    permissionCache.set(cacheKey, rolePermissions);
    clearCacheEntry(cacheKey);
    
    return rolePermissions;
  } catch (error) {
    console.error('Error fetching role permissions:', error);
    return null;
  }
};

// Get all role definitions
export const getAllRoles = async (): Promise<RoleDefinition[]> => {
  const cacheKey = 'all_roles';
  
  if (permissionCache.has(cacheKey)) {
    return permissionCache.get(cacheKey);
  }

  try {
    const rolesQuery = query(
      collection(db, 'roles'),
      orderBy('order', 'asc')
    );
    
    const snapshot = await getDocs(rolesQuery);
    
    if (snapshot.empty) {
      // Initialize with default roles if none exist
      await initializeDefaultRoles();
      return await getAllRoles(); // Recursive call after initialization
    }
    
    const roles = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as RoleDefinition));
    
    permissionCache.set(cacheKey, roles);
    clearCacheEntry(cacheKey);
    
    return roles;
  } catch (error) {
    console.error('Error fetching roles:', error);
    return [];
  }
};

// Update role permissions
export const updateRolePermissions = async (
  roleId: string,
  permissions: string[],
  updatedBy: string,
  customSettings?: {
    dataAccessLevel: DataAccessLevel;
    canExport: boolean;
    canBulkEdit: boolean;
    canDeletePermanently: boolean;
  }
): Promise<void> => {
  try {
    const rolePermRef = doc(db, 'rolePermissions', roleId);
    const existingDoc = await getDoc(rolePermRef);
    
    const previousPermissions = existingDoc.exists() 
      ? existingDoc.data().permissions 
      : [];
    
    const updateData: any = {
      permissions,
      updatedAt: Timestamp.now()
    };
    
    if (customSettings) {
      updateData.customSettings = customSettings;
    }
    
    await updateDoc(rolePermRef, updateData);
    
    // Log the change
    await logPermissionChange({
      roleId,
      changedBy: updatedBy,
      changeType: 'modify',
      permissions,
      previousPermissions,
      timestamp: Timestamp.now()
    });
    
    // Clear cache
    permissionCache.delete(`role_permissions_${roleId}`);
  } catch (error) {
    console.error('Error updating role permissions:', error);
    throw error;
  }
};

// Create a new custom role
export const createCustomRole = async (
  role: Omit<RoleDefinition, 'id' | 'createdAt' | 'updatedAt'>,
  permissions: string[],
  createdBy: string
): Promise<string> => {
  try {
    const roleId = `role_${Date.now()}`;
    const timestamp = Timestamp.now();
    
    // Create role definition
    await setDoc(doc(db, 'roles', roleId), {
      ...role,
      id: roleId,
      isCustom: true,
      isSystemRole: false,
      createdAt: timestamp,
      updatedAt: timestamp
    });
    
    // Create role permissions
    await setDoc(doc(db, 'rolePermissions', roleId), {
      roleId,
      permissions,
      menuAccess: [],
      customSettings: {
        dataAccessLevel: DataAccessLevel.OWN,
        canExport: false,
        canBulkEdit: false,
        canDeletePermanently: false
      },
      createdAt: timestamp,
      updatedAt: timestamp
    });
    
    // Log the creation
    await logPermissionChange({
      roleId,
      changedBy: createdBy,
      changeType: 'grant',
      permissions,
      timestamp
    });
    
    // Clear cache
    permissionCache.delete('all_roles');
    
    return roleId;
  } catch (error) {
    console.error('Error creating custom role:', error);
    throw error;
  }
};

// Delete a custom role
export const deleteCustomRole = async (roleId: string): Promise<void> => {
  try {
    const roleDoc = await getDoc(doc(db, 'roles', roleId));
    
    if (!roleDoc.exists()) {
      throw new Error('Role not found');
    }
    
    const roleData = roleDoc.data() as RoleDefinition;
    
    if (roleData.isSystemRole) {
      throw new Error('Cannot delete system roles');
    }
    
    // Delete role definition and permissions
    const batch = writeBatch(db);
    batch.delete(doc(db, 'roles', roleId));
    batch.delete(doc(db, 'rolePermissions', roleId));
    await batch.commit();
    
    // Clear cache
    permissionCache.delete('all_roles');
    permissionCache.delete(`role_permissions_${roleId}`);
  } catch (error) {
    console.error('Error deleting role:', error);
    throw error;
  }
};

// Get partner-specific role configurations
export const getPartnerRoles = async (partnerId: string): Promise<PartnerRole[]> => {
  try {
    const partnerRolesQuery = query(
      collection(db, 'partnerRoles'),
      where('partnerId', '==', partnerId)
    );
    
    const snapshot = await getDocs(partnerRolesQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as PartnerRole));
  } catch (error) {
    console.error('Error fetching partner roles:', error);
    return [];
  }
};

// Update partner role configuration
export const updatePartnerRole = async (
  partnerId: string,
  roleId: string,
  updates: Partial<PartnerRole>
): Promise<void> => {
  try {
    const partnerRoleQuery = query(
      collection(db, 'partnerRoles'),
      where('partnerId', '==', partnerId),
      where('roleId', '==', roleId)
    );
    
    const snapshot = await getDocs(partnerRoleQuery);
    
    if (snapshot.empty) {
      // Create new partner role configuration
      await setDoc(doc(collection(db, 'partnerRoles')), {
        partnerId,
        roleId,
        ...updates,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } else {
      // Update existing
      const docRef = snapshot.docs[0].ref;
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error('Error updating partner role:', error);
    throw error;
  }
};

// Check if a user has a specific permission
export const checkUserPermission = async (
  userId: string,
  permission: string
): Promise<boolean> => {
  try {
    // Get user's role
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      return false;
    }
    
    const userRole = userDoc.data().role;
    
    // Super users have all permissions
    if (userRole === UserRole.PROPCLOUDAI_SUPER_USER) {
      return true;
    }
    
    // Get role permissions
    const rolePermissions = await getRolePermissions(userRole);
    
    if (!rolePermissions) {
      return false;
    }
    
    return rolePermissions.permissions.includes(permission);
  } catch (error) {
    console.error('Error checking user permission:', error);
    return false;
  }
};

// Log permission changes for audit
const logPermissionChange = async (log: PermissionChangeLog): Promise<void> => {
  try {
    await setDoc(doc(collection(db, 'permissionChangeLogs')), log);
  } catch (error) {
    console.error('Error logging permission change:', error);
  }
};

// Initialize default categories
const initializeDefaultCategories = async (): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    DEFAULT_PERMISSION_CATEGORIES.forEach(category => {
      batch.set(doc(db, 'permissionCategories', category.id), category);
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error initializing default categories:', error);
  }
};

// Initialize default permissions
const initializeDefaultPermissions = async (): Promise<void> => {
  try {
    const batch = writeBatch(db);
    const timestamp = Timestamp.now();
    
    // Define default permissions for each category
    const defaultPermissions: Permission[] = [
      // Development Management
      {
        id: 'developments.create',
        categoryId: 'development_management',
        name: 'create_development',
        displayName: 'Create Development',
        description: 'Create new property developments',
        resource: PERMISSION_RESOURCES.DEVELOPMENTS,
        action: PERMISSION_ACTIONS.CREATE,
        isSystemPermission: false,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: 'developments.update',
        categoryId: 'development_management',
        name: 'update_development',
        displayName: 'Update Development',
        description: 'Edit existing developments',
        resource: PERMISSION_RESOURCES.DEVELOPMENTS,
        action: PERMISSION_ACTIONS.UPDATE,
        isSystemPermission: false,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: 'developments.delete',
        categoryId: 'development_management',
        name: 'delete_development',
        displayName: 'Delete Development',
        description: 'Delete developments',
        resource: PERMISSION_RESOURCES.DEVELOPMENTS,
        action: PERMISSION_ACTIONS.DELETE,
        isSystemPermission: false,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: 'developments.view_all',
        categoryId: 'development_management',
        name: 'view_all_developments',
        displayName: 'View All Developments',
        description: 'View all developments in the system',
        resource: PERMISSION_RESOURCES.DEVELOPMENTS,
        action: PERMISSION_ACTIONS.VIEW_ALL,
        isSystemPermission: false,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      // User Management
      {
        id: 'users.create',
        categoryId: 'user_management',
        name: 'create_user',
        displayName: 'Create User',
        description: 'Create new users',
        resource: PERMISSION_RESOURCES.USERS,
        action: PERMISSION_ACTIONS.CREATE,
        isSystemPermission: false,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: 'users.update',
        categoryId: 'user_management',
        name: 'update_user',
        displayName: 'Update User',
        description: 'Edit user details',
        resource: PERMISSION_RESOURCES.USERS,
        action: PERMISSION_ACTIONS.UPDATE,
        isSystemPermission: false,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: 'users.delete',
        categoryId: 'user_management',
        name: 'delete_user',
        displayName: 'Delete User',
        description: 'Delete users',
        resource: PERMISSION_RESOURCES.USERS,
        action: PERMISSION_ACTIONS.DELETE,
        isSystemPermission: false,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: 'roles.manage',
        categoryId: 'user_management',
        name: 'manage_roles',
        displayName: 'Manage Roles',
        description: 'Create and modify user roles',
        resource: PERMISSION_RESOURCES.ROLES,
        action: PERMISSION_ACTIONS.MANAGE,
        isSystemPermission: true,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      // Sales & Reservations
      {
        id: 'reservations.create',
        categoryId: 'sales_reservations',
        name: 'create_reservation',
        displayName: 'Create Reservation',
        description: 'Create unit reservations',
        resource: PERMISSION_RESOURCES.RESERVATIONS,
        action: PERMISSION_ACTIONS.CREATE,
        isSystemPermission: false,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: 'reservations.extend',
        categoryId: 'sales_reservations',
        name: 'extend_reservation',
        displayName: 'Extend Reservation',
        description: 'Extend reservation periods',
        resource: PERMISSION_RESOURCES.RESERVATIONS,
        action: PERMISSION_ACTIONS.EXTEND,
        isSystemPermission: false,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: 'deals.create',
        categoryId: 'sales_reservations',
        name: 'create_deal',
        displayName: 'Create Deal',
        description: 'Create sales deals',
        resource: PERMISSION_RESOURCES.DEALS,
        action: PERMISSION_ACTIONS.CREATE,
        isSystemPermission: false,
        createdAt: timestamp,
        updatedAt: timestamp
      },
      // Add more default permissions...
    ];
    
    defaultPermissions.forEach(permission => {
      batch.set(doc(db, 'permissions', permission.id), permission);
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error initializing default permissions:', error);
  }
};

// Initialize default roles
const initializeDefaultRoles = async (): Promise<void> => {
  try {
    const batch = writeBatch(db);
    const timestamp = Timestamp.now();
    
    const defaultRoles: RoleDefinition[] = [
      // PropCloudAI System Roles
      {
        id: UserRole.PROPCLOUDAI_SUPER_USER,
        name: 'propcloudai_super_user',
        displayName: 'PropCloudAI Super User',
        description: 'Full system access with all permissions',
        isSystemRole: true,
        isCustom: false,
        order: 1,
        color: '#6eb041',
        icon: 'Shield',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: UserRole.PROPCLOUDAI_USER_MANAGEMENT,
        name: 'propcloudai_user_management',
        displayName: 'PropCloudAI User Management',
        description: 'Manage users and permissions',
        isSystemRole: true,
        isCustom: false,
        order: 2,
        color: '#3b82f6',
        icon: 'UserCog',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: UserRole.PROPCLOUDAI_CRM,
        name: 'propcloudai_crm',
        displayName: 'PropCloudAI CRM',
        description: 'CRM system management',
        isSystemRole: true,
        isCustom: false,
        order: 3,
        color: '#8b5cf6',
        icon: 'Users',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: UserRole.PROPCLOUDAI_ADVERTISING,
        name: 'propcloudai_advertising',
        displayName: 'PropCloudAI Advertising',
        description: 'Advertising and marketing management',
        isSystemRole: true,
        isCustom: false,
        order: 4,
        color: '#ec4899',
        icon: 'Megaphone',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      // PropInvest HQ Roles
      {
        id: UserRole.HQ_MANAGER,
        name: 'hq_manager',
        displayName: 'HQ Manager',
        description: 'HQ management operations',
        isSystemRole: true,
        isCustom: false,
        order: 5,
        color: '#f59e0b',
        icon: 'Settings',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: UserRole.HQ_ADMIN,
        name: 'hq_admin',
        displayName: 'HQ Admin',
        description: 'HQ administrative access',
        isSystemRole: true,
        isCustom: false,
        order: 6,
        color: '#10b981',
        icon: 'Shield',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: UserRole.HQ_BONDS,
        name: 'hq_bonds',
        displayName: 'HQ Bonds',
        description: 'Bond origination and finance',
        isSystemRole: true,
        isCustom: false,
        order: 7,
        color: '#ef4444',
        icon: 'Banknote',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: UserRole.HQ_CONSULTANT_LVL1,
        name: 'hq_consultant_lvl1',
        displayName: 'HQ Consultant (Level 1)',
        description: 'Sales consultant with 30% commission',
        isSystemRole: true,
        isCustom: false,
        order: 8,
        color: '#6366f1',
        icon: 'Briefcase',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
        name: 'hq_independent_consultant_lvl2',
        displayName: 'HQ Independent Consultant (Level 2)',
        description: 'Independent consultant with 50% commission',
        isSystemRole: true,
        isCustom: false,
        order: 9,
        color: '#8b5cf6',
        icon: 'UserCheck',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: UserRole.HQ_REFERRAL_MANDATE,
        name: 'hq_referral_mandate',
        displayName: 'HQ Referral Mandate',
        description: 'Referral mandate with 5% commission',
        isSystemRole: true,
        isCustom: false,
        order: 10,
        color: '#64748b',
        icon: 'Share',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      {
        id: UserRole.INDEPENDENT_PARTNER_LVL3,
        name: 'independent_partner_lvl3',
        displayName: 'Independent Partner (Level 3)',
        description: 'Partner with 70% commission',
        isSystemRole: true,
        isCustom: false,
        order: 11,
        color: '#f97316',
        icon: 'Building',
        createdAt: timestamp,
        updatedAt: timestamp
      },
      // Partner Office Roles
      {
        id: UserRole.PARTNER_AGENT_LVL3,
        name: 'partner_agent_lvl3',
        displayName: 'Partner Agent (Level 3)',
        description: 'Agent working under a partner',
        isSystemRole: true,
        isCustom: false,
        order: 12,
        color: '#06b6d4',
        icon: 'User',
        createdAt: timestamp,
        updatedAt: timestamp
      }
    ];
    
    defaultRoles.forEach(role => {
      batch.set(doc(db, 'roles', role.id), role);
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error initializing default roles:', error);
  }
};

// Get permission templates
export const getPermissionTemplates = async (): Promise<PermissionTemplate[]> => {
  try {
    const templatesQuery = query(collection(db, 'permissionTemplates'));
    const snapshot = await getDocs(templatesQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as PermissionTemplate));
  } catch (error) {
    console.error('Error fetching permission templates:', error);
    return [];
  }
};

// Apply permission template to a role
export const applyPermissionTemplate = async (
  roleId: string,
  templateId: string,
  updatedBy: string
): Promise<void> => {
  try {
    const templateDoc = await getDoc(doc(db, 'permissionTemplates', templateId));
    
    if (!templateDoc.exists()) {
      throw new Error('Template not found');
    }
    
    const template = templateDoc.data() as PermissionTemplate;
    await updateRolePermissions(roleId, template.permissions, updatedBy);
  } catch (error) {
    console.error('Error applying permission template:', error);
    throw error;
  }
};